// Read + write "Who can use Groups" — the master switch
// (feature_flags.groups_enabled) plus public.groups_rollout (mode: admins ·
// testers · everyone) and public.groups_testers (named non-admin accounts who
// preview Groups before it opens to Everyone), migration
// 20261018120000_groups_audience_core.sql.
//
// Modelled on group_chapters/useClassroomSettings.ts:
// 1. UNREADABLE IS NOT "ADMINS ONLY" — AND NOT "OFF" EITHER. `state` is null
//    and `unreadable` true when EITHER read (groups_rollout OR the
//    feature_flags groups_enabled row) fails — an RLS error, a network
//    error, or (most likely) the dashboard auto-deployed before this
//    migration applied. Drawing "Admins only" for an unreadable row would be
//    a guess the owner could act on (e.g. widening on top of a state we
//    never actually read) — and MA-F2-01 (final-review round 2) shows
//    drawing "Off" is just as wrong: a failed feature_flags read used to
//    fall through to `masterOn = false`, showing the kill switch as already
//    engaged (and disabling the Off segment's own no-op click guard) while
//    the read had simply failed. Both reads' errors — the rollout row's `re`
//    and the groups_enabled flag row's own `fe` — gate the SAME unreadable
//    branch below.
// 2. The FOUR-state ladder this page draws (Off · Admins only ·
//    Admins + testers · Everyone) is NOT groups_rollout.mode alone: "Off" is
//    `groups_enabled = false` — the row's own audience RLS still hides
//    everything from non-admins even while `mode` is left at whatever it was.
//    Only once the master is on does `mode` decide the rest.
// 3. `admin_set_groups_access(p_state)` writes the master flag and the
//    rollout row in ONE transaction, so this hook never has to sequence two
//    separate writes (and never half-applies a change).
//
// `setState`/`addTesters`/`removeTester` throw on error and re-read
// afterwards, so the card always renders what the database holds.

import { useCallback, useEffect, useState } from 'react'
import { useInvalidate } from '@refinedev/core'
import { supabaseClient } from '../../lib/supabaseClient'

/** off · admins · testers · everyone — the CHECK on groups_rollout.mode, plus "off" for the master switch. */
export type GroupsAudienceState = 'off' | 'admins' | 'testers' | 'everyone'

export interface GroupsTester {
  user_id: string
  note: string
  created_at: string
  /** From profiles — 'Unknown member' if the profile row is gone. */
  name: string
  class_year: string | null
}

export interface GroupsAudienceData {
  /** null while loading, or when the row can't be read (see `unreadable`). */
  state: GroupsAudienceState | null
  /** True when groups_rollout could not be read — never drawn as "Admins only". */
  unreadable: boolean
  /** True until the first read finishes. */
  loading: boolean
  testers: GroupsTester[]
  /**
   * MA-F3-02 (final-review round 3): true when the `groups_testers` read
   * itself failed — independent of `unreadable` above (state/rollout can
   * read fine while this one 5xxs). Never draw this as "No testers yet.":
   * the empty state and a failed read look identical unless this flag says
   * otherwise, and the widening confirm's tester count would otherwise
   * silently claim 0 testers while real ones are about to be admitted.
   */
  testersUnreadable: boolean
  testerIds: Set<string>
  refetch: () => Promise<void>
  /** Calls admin_set_groups_access(p_state); throws on error. */
  setState: (next: GroupsAudienceState) => Promise<void>
  /** Inserts { user_id } rows into groups_testers; throws on error (e.g. not_allowed). */
  addTesters: (userIds: string[]) => Promise<void>
  removeTester: (userId: string) => Promise<void>
}

export function useGroupsAudience(): GroupsAudienceData {
  // SA-03 (fix round 1): `admin_set_groups_access` writes `groups_enabled`,
  // a row this hook's OWN refetch() re-reads via the raw supabaseClient —
  // but page.tsx's stat tile and the sub-flag "Has no effect while Groups
  // is off." hints read that SAME row through Refine's `useList('feature_flags')`,
  // a separate cache refetch() never touches. Without this, the card shows
  // the new state correctly while the rest of the page keeps showing the
  // one it just replaced, until an unrelated refetch (a reload, a 5-minute
  // staleTime) happens to catch up.
  const invalidate = useInvalidate()
  const [state, setStateValue] = useState<GroupsAudienceState | null>(null)
  const [unreadable, setUnreadable] = useState(false)
  const [testers, setTesters] = useState<GroupsTester[]>([])
  const [testersUnreadable, setTestersUnreadable] = useState(false)
  const [loading, setLoading] = useState(true)

  const refetch = useCallback(async () => {
    try {
      // MA-F2-01 (final-review round 2): the feature_flags read's OWN error
      // must be checked exactly like the rollout read's `re` below. A failed
      // GET resolves `{ data: null, error }` (postgres-js swallows the fetch
      // failure rather than throwing), so dropping this destructure silently
      // fell through to `masterOn = flagRow?.enabled === true` -> false ->
      // "off" — drawing the kill switch as already engaged instead of
      // "Unreadable" (rule #1 above: never draw a state we didn't read).
      const [{ data: flagRow, error: fe }, { data: rolloutRow, error: re }] = await Promise.all([
        supabaseClient.from('feature_flags').select('enabled').eq('key', 'groups_enabled').maybeSingle(),
        supabaseClient.from('groups_rollout').select('mode').eq('id', true).maybeSingle(),
      ])
      if (fe || re || !rolloutRow) {
        setStateValue(null)
        setUnreadable(true)
        setTesters([])
        setTestersUnreadable(false)
        // NOT setTestersUnreadable(true) here: `unreadable` above already
        // covers "the whole card failed to read" (its own Pill + Try again),
        // and the testers query never even ran in this branch. MA-F3-02 is
        // specifically about the testers read failing AFTER state/rollout
        // already succeeded — keep this card's two failure signals disjoint
        // so they never both render an "Unreadable" Pill at once.
        return
      }
      const masterOn = flagRow?.enabled === true
      const mode = (rolloutRow.mode as GroupsAudienceState | undefined) ?? 'admins'
      setStateValue(masterOn ? mode : 'off')
      setUnreadable(false)

      const { data: testerRows, error: te } = await supabaseClient
        .from('groups_testers')
        .select('user_id, note, created_at')
        .order('created_at', { ascending: false })
      if (te) {
        // MA-F3-02: a failed THIRD read (after state/rollout already
        // succeeded) must not be silently drawn as "0 testers" — that
        // empties the widening confirm's own count and hides real testers
        // from GroupMembersTable's pills, even though they are still on the
        // list and will still be admitted once the owner confirms.
        setTesters([])
        setTestersUnreadable(true)
        return
      }
      setTestersUnreadable(false)
      const rows = (testerRows ?? []) as { user_id: string; note: string | null; created_at: string }[]
      const ids = rows.map((t) => t.user_id)
      let infoById = new Map<string, { name: string; class_year: string | null }>()
      if (ids.length > 0) {
        const { data: profiles } = await supabaseClient.from('profiles').select('user_id, name, class_year').in('user_id', ids)
        infoById = new Map(
          ((profiles ?? []) as { user_id: string; name: string | null; class_year: string | null }[]).map((p) => [
            p.user_id,
            { name: p.name || 'Unknown member', class_year: p.class_year },
          ]),
        )
      }
      setTesters(
        rows.map((t) => ({
          user_id: t.user_id,
          note: t.note ?? '',
          created_at: t.created_at,
          name: infoById.get(t.user_id)?.name ?? 'Unknown member',
          class_year: infoById.get(t.user_id)?.class_year ?? null,
        })),
      )
    } catch {
      // Same disjoint-signal reasoning as the branch above: `unreadable`
      // alone covers a total read failure (its own Pill + Try again), so
      // `testersUnreadable` is left as-is here too.
      setStateValue(null)
      setUnreadable(true)
      setTesters([])
      setTestersUnreadable(false)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refetch()
  }, [refetch])

  const setState = useCallback(
    async (next: GroupsAudienceState) => {
      const { error } = await supabaseClient.rpc('admin_set_groups_access', { p_state: next })
      if (error) throw new Error(error.message)
      await refetch()
      // SA-03: refetch() only refreshes THIS card's own raw-client read;
      // invalidate the Refine-backed `feature_flags` list too, or
      // page.tsx's stat tile / "Has no effect while Groups is off." hints
      // go stale on the very same change.
      invalidate({ resource: 'feature_flags', invalidates: ['list'] })
    },
    [refetch, invalidate],
  )

  const addTesters = useCallback(
    async (userIds: string[]) => {
      if (userIds.length === 0) return
      const { error } = await supabaseClient.from('groups_testers').insert(userIds.map((user_id) => ({ user_id })))
      if (error) throw new Error(error.message)
      await refetch()
    },
    [refetch],
  )

  const removeTester = useCallback(
    async (userId: string) => {
      const { error } = await supabaseClient.from('groups_testers').delete().eq('user_id', userId)
      if (error) throw new Error(error.message)
      await refetch()
    },
    [refetch],
  )

  return {
    state,
    unreadable,
    loading,
    testers,
    testersUnreadable,
    testerIds: new Set(testers.map((t) => t.user_id)),
    refetch,
    setState,
    addTesters,
    removeTester,
  }
}
